/**
 * Звідки панель «Сьогодні» бере дані й куди пише (`TodaySource`): після входу — `GET
 * /api/v1/activities/today`, операції поведінки задачі `POST /api/v1/records/activity/{id}/
 * complete|reschedule` з `If-Match` (версія задачі) і `Idempotency-Key`, «Скасувати» — `POST
 * /api/v1/undo` токеном з відповіді (tasks.md, розд. 4).
 */
import type { AppMeta, RecordWriteResult, TodayItem, UndoResult } from '@clientline/contracts';
import { type QueryKey, queryOptions } from '@tanstack/react-query';
import { api } from '../../platform/api.ts';
import type { TodayData, TodayDue } from './model.ts';

/** Відповідь зміни: нова версія задачі й токен «Скасувати» (`null` — дія незворотна). */
export interface TodayWrite {
  version: number | null;
  undo: string | null;
}

export interface TodaySource {
  key: QueryKey;
  /** Живе оновлення подіями SSE (tasks-today.md, розд. 6). */
  live: boolean;
  /**
   * Результат задачі — панеллю на місці (E8a-W3, `POST …/complete` з результатом і кроком). Немає —
   * «Виконати» з обов'язковим результатом відкриває картку задачі.
   */
  outcomePanel?: boolean;
  /**
   * Секція «Дзвінки без результату» (Г2-W1; calls.md, розд. 4): мої дзвінки за 7 днів окремим запитом
   * списку. Немає — секції немає.
   */
  calls?: boolean;
  fetch: (signal?: AbortSignal) => Promise<TodayData>;
  complete: (item: TodayItem, key: string) => Promise<TodayWrite>;
  reschedule: (item: TodayItem, due: TodayDue, key: string) => Promise<TodayWrite>;
  /** Скасувати дію токеном; `version` — версія задачі після скасування (`null` — невідомо). */
  undo: (token: string, key: string) => Promise<{ version: number | null }>;
}

/** Модуль і сутність задач (`modules/activities`): маршрути модуля — під `/api/v1/<модуль>`. */
const MODULE = 'activities';
const ENTITY = 'activity';
/** Сутність задач: пункт навігації з нею несе лічильник «Задачі N». */
export const TASK_ENTITY = ENTITY;

/** Ключ кешу платформи: префікс `p` скидає вихід і подія `reset` потоку (events.ts). */
export const PLATFORM_TODAY_KEY = ['p', 'today'] as const;

/** Панель є, лише коли модуль увімкнено й людина читає задачі (`/meta` ховає сутність без `read`). */
export function todayAvailable(meta: AppMeta): boolean {
  return meta.modules.includes(MODULE) && meta.entities.some((e) => e.key === ENTITY);
}

const written = (result: RecordWriteResult): TodayWrite => ({
  version: result.version,
  undo: result.undo?.token ?? null,
});

const operation = (item: TodayItem, name: string, body: object, key: string) =>
  api<RecordWriteResult>('POST', `/api/v1/records/${ENTITY}/${item.id}/${name}`, {
    body,
    ifMatch: item.version,
    key,
  }).then(written);

export const platformToday: TodaySource = {
  key: PLATFORM_TODAY_KEY,
  live: true,
  outcomePanel: true,
  calls: true,
  fetch: (signal) =>
    api<TodayData>('GET', `/api/v1/${MODULE}/today`, { ...(signal ? { signal } : {}) }),
  complete: (item, key) => operation(item, 'complete', {}, key),
  reschedule: (item, due, key) => operation(item, 'reschedule', due, key),
  undo: (token, key) =>
    api<UndoResult>('POST', '/api/v1/undo', { body: { tokens: [token] }, key }).then((r) => ({
      version: r.records.find((x) => x.entity === ENTITY)?.version ?? null,
    })),
};

export const todayQueryOf = (source: TodaySource) =>
  queryOptions({ queryKey: source.key, queryFn: ({ signal }) => source.fetch(signal) });
